import React, { useState, useEffect } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Search, PencilSquare, Trash, PlusCircle, Backspace, Diagram3, Building, Receipt, Download, Copy,} from "react-bootstrap-icons"; // Iconos de Bootstrap
import { AlertError, AlertLoading } from "../helpers/AlertHelper";
import { formatDate, moment } from "../helpers/momentHelper";
import formatCurrency from "../helpers/currencyHelper";
import FormModal from "../components/modals/FormModal";
import TableManagementThreeButtons from "../components/tables/TableManagementThreeButtoms";
import SearchSelect from "../components/form/SearchSelect";
import ExpensesProjectsService from "../services/ExpensesProjectsService";
import ProjectService from "../services/ProjectsService";
import ProvidersService from "../services/ProvidersService";
import ExpensesProjectsForm from "./ExpensesProjectsForm";
import { exportToExcel } from "../utils/exportToExcel";

const ExpensesProjects = ({
  showSearchBar = true,
  selectedProject: selectedProjectProp,
  yearFrom: yearFromProp,
  yearTo: yearToProp,
}) => {
  const intl = useIntl();
  const [expenseProjectId, setExpenseProjectId] = useState(null);
  const [expensesProjects, setExpensesProjects] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [currPage, setCurrPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("asc");
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  const [formattedData, setformattedData] = useState([]);
  const [projectsList, setProjectsList] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [providersList, setProvidersList] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("");

  // Setting modal title
  const [modalTitle, setModalTitle] = useState(false);

  // Setting modal icon
  const [modalIcon, setModalIcon] = useState(false);

  // Open modal form control to add/edit element
  const [modalTrigger, setModalTrigger] = useState(false);

  // Setting modal action enum[add,edit,delete]
  const [modalAction, setModalAction] = useState(false);

  // Fetch projects
  const fetchProjects = async () => {
    try {
      const response = await ProjectService.getProjects(1, "", 2000);
      setProjectsList(response.data.projects);
    } catch (err) {
      setError(err);
    }
  };

  // Fetch providers
  const fetchProviders = async () => {
    try {
      const response = await ProvidersService.getProviders(1, "", 2000);
      setProvidersList(response.data.providers);
    } catch (err) {
      setError(err);
    }
  };

  // Fetch projects expenses
  const fetchExpensesProjects = async () => {
    try {
      const response = await ExpensesProjectsService.getExpensesProjects(
        currPage,
        search,
        perPage,
        sortBy,
        sortOrder,
        selectedProject,
        selectedProvider,
        yearFrom,
        yearTo
      );
      setExpensesProjects(response.data.expensesProjects);
      setTotalRows(response.data.pagination.totalItems);
      setIsLoaded(true);
    } catch (err) {
      setIsLoaded(false);
      setError(err);
    }
  };

  const handleModal = () => {
    setModalTrigger(false);
    fetchExpensesProjects();
  };

  const handleSelectProjectChange = (e) => {
    setSelectedProject(e.target.value);
    setCurrPage(1);
  };

  const handleSelectProviderChange = (e) => {
    setSelectedProvider(e.target.value);
    setCurrPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrPage(1); // Reinicia a la primera página cuando se busca
  };

  const handleSearchClean = () => {
    setSearch("");
    setSelectedProject("");
    setSelectedProvider("");
    setYearFrom("");
    setYearTo("");
    setCurrPage(1); // Reinicia a la primera página cuando se busca
  };

  useEffect(() => {
    if (selectedProjectProp !== undefined) {
      setSelectedProject(selectedProjectProp || "");
    }
  }, [selectedProjectProp]);

  useEffect(() => {
    if (yearFromProp !== undefined) {
      setYearFrom(yearFromProp || "");
    }
  }, [yearFromProp]);

  useEffect(() => {
    if (yearToProp !== undefined) {
      setYearTo(yearToProp || "");
    }
  }, [yearToProp]);

  // fetch projects when start
  useEffect(() => {
    fetchProjects();
    fetchProviders();
  }, []);

  // Re-fetch data on page/perPage change
  useEffect(() => {
    fetchExpensesProjects();
  }, [
    currPage,
    perPage,
    search,
    sortBy,
    sortOrder,
    selectedProject,
    selectedProvider,
    yearFrom,
    yearTo,
  ]);

  const handleButtonClick = ({ data, type }) => {
    // Definir un objeto para mapear tipos a títulos e íconos
    const modalConfig = {
      add: {
        title: (
          <FormattedMessage id="expensesAdd" defaultMessage="Add expense" />
        ),
        icon: <PlusCircle />,
      },
      edit: {
        title: (
          <FormattedMessage id="expensesEdit" defaultMessage="Edit expense" />
        ),
        icon: <PencilSquare />,
      },
      delete: {
        title: (
          <FormattedMessage
            id="expensesDelete"
            defaultMessage="Delete expense"
          />
        ),
        icon: <Trash />,
      },
      duplicate: {
        title: <FormattedMessage id="textDuplicate" defaultMessage="Duplicate expense" />,
        icon: <Copy />,
      },
    };

    // Establecer la configuración del modal dependiendo del tipo
    const config = modalConfig[type] || {};

    if (config) {
      setExpenseProjectId(type === "add" ? false : data._id); // Si 'add', incomeid será falso
      setModalTitle(config.title); // Set title from the config
      setModalIcon(config.icon); // Set icon from the config
      setModalAction(type);
      setModalTrigger(true);
    }
  };

  const handleRowsPerPageChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrPage(page);
  };

  const handleSort = async (column, sortDirection) => {
    setSortOrder(sortDirection);
    setSortBy(column.id);
    setCurrPage(1);
  };

  const columns = [
    {
      id: "projectId",
      name: <FormattedMessage id="expensesProject" defaultMessage="Project" />,
      selector: (row) => row.projectId.name,
      sortable: true,
    },
    {
      id: "providerId",
      name: (
        <FormattedMessage id="expensesProvider" defaultMessage="Provider" />
      ),
      selector: (row) => row.providerId.name,
      sortable: true,
    },
    {
      id: "expenseNumber",
      name: (
        <FormattedMessage id="expensesNumber" defaultMessage="Expense number" />
      ),
      selector: (row) => row.expenseNumber,
      sortable: true,
    },
    {
      id: "type",
      name: <FormattedMessage id="expensesType" defaultMessage="Type" />,
      selector: (row) => row.type,
      sortable: true,
    },
    {
      id: "concept",
      name: <FormattedMessage id="expensesConcept" defaultMessage="Concept" />,
      selector: (row) => row.concept,
    },
    {
      id: "date",
      name: (
        <FormattedMessage id="expensesDate" defaultMessage="Expense date" />
      ),
      selector: (row) => formatDate(row.date, "DD/MM/YYYY"),
      sortable: true,
    },
    {
      id: "paymentDate",
      name: (
        <FormattedMessage
          id="expensesPaymentDate"
          defaultMessage="Expense payment date"
        />
      ),
      selector: (row) => formatDate(row.paymentDate, "DD/MM/YYYY"),
      sortable: true,
    },
    {
      id: "amount",
      name: <FormattedMessage id="expensesAmount" defaultMessage="Amount" />,
      selector: (row) => formatCurrency(row.amount),
      sortable: true,
      right: "true",
    },
  ];
  const headersMap = {
    projectId: "Proyecto",
    providerId: "Proveedor",
    expenseNumber: "Número de gasto",
    type: "Tipo",
    concept: "Concepto",
    date: "Fecha",
    paymentDate: "Fecha de pago",
    amount: "Importe",
  };

  useEffect(() => {
    let formattedExpensesProjects = expensesProjects.map((item) => ({
      [headersMap.projectId]: item.projectId?.name || "",
      [headersMap.providerId]: item.providerId?.name || "",
      [headersMap.expenseNumber]: item.expenseNumber || "",
      [headersMap.type]: item.type || "",
      [headersMap.concept]: item.concept || "",
      [headersMap.date]: item.date
        ? moment(item.date).format("DD/MM/YYYY")
        : "",
      [headersMap.paymentDate]: item.paymentDate
        ? moment(item.paymentDate).format("DD/MM/YYYY")
        : "",
      [headersMap.amount]: formatCurrency(item.amount || 0),
    }));

    setformattedData(formattedExpensesProjects);
  }, [expensesProjects]);

  return (
    <Container fluid className="p-0">
      {showSearchBar && (
        <Row className="p-1 bg-secondary">
          <Col>
            <InputGroup>
              <InputGroup.Text>
                <Diagram3 />
              </InputGroup.Text>
              <SearchSelect
                fieldName={"projectId"}
                selected={selectedProject}
                onChange={handleSelectProjectChange}
                options={projectsList}
              />
              <InputGroup.Text>
                <Building />
              </InputGroup.Text>
              <SearchSelect
                fieldName={"providerId"}
                selected={selectedProvider}
                onChange={handleSelectProviderChange}
                options={providersList}
              />
              <InputGroup.Text>
                <Search />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={intl.formatMessage({
                  id: "textSearch",
                  defaultMessage: "Search expenses...",
                })}
                value={search}
                onChange={handleSearchChange}
              />
              <Button onClick={handleSearchClean} variant="light">
                <Backspace />{" "}
                <FormattedMessage id="textClean" defaultMessage="Clean" />
              </Button>

              <Button
                onClick={() => handleButtonClick({ data: null, type: "add" })}
                variant="primary"
              >
                <PlusCircle />{" "}
                <FormattedMessage id="textAdd" defaultMessage="Add" />
              </Button>
              {formattedData.length > 0 && (
                <Button
                  onClick={() =>
                    exportToExcel(
                      formattedData,
                      "gastos.xlsx",
                      "Tipo",
                      "Importe"
                    )
                  }
                  variant="success"
                >
                  <Download />{" "}
                  <FormattedMessage
                    id="downloadExcel"
                    defaultMessage="Export"
                  />
                </Button>
              )}
            </InputGroup>
          </Col>
        </Row>
      )}

      {isLoaded ? (
        <>
          <TableManagementThreeButtons
            icon={<Receipt />}
            title={
              <FormattedMessage
                id="navExpensesProjects"
                defaultMessage="Projects Expenses"
              />
            }
            columns={columns}
            data={expensesProjects}
            paginationPerPage={perPage}
            paginationTotalRows={totalRows}
            onChangePage={setCurrPage}
            onChangeRowsPerPage={handleRowsPerPageChange}
            onSort={handleSort}
            onActionClick={handleButtonClick}
          />
        </>
      ) : error ? (
        <AlertError error={error} />
      ) : (
        <AlertLoading isLoaded={isLoaded} />
      )}

      <FormModal
        title={modalTitle}
        icon={modalIcon}
        body={ExpensesProjectsForm}
        refreshData={handleModal}
        modalTrigger={modalTrigger}
        elementId={expenseProjectId}
        action={modalAction}
      />
    </Container>
  );
};

export default ExpensesProjects;
